/**
 * OAuthButton
 *
 * Standardized OAuth button used for Google/Apple sign-in across auth modals.
 * Implements Figma design specs with consistent icon + text layout.
 */
"use client";

import Image from "next/image";

import { colors, modalSpacing } from "@/styles/design-tokens";

type OAuthProvider = "google" | "apple";

interface OAuthButtonProps {
  provider: OAuthProvider;
  onClick: () => void;
  disabled?: boolean;
  label?: string;
  className?: string;
}

const PROVIDER_CONFIG = {
  google: {
    icon: "/icons/google-icon.svg",
    defaultLabel: "Continue with Google",
  },
  apple: {
    icon: "/icons/apple-icon.svg",
    defaultLabel: "Continue with Apple",
  },
} as const;

export function OAuthButton({
  provider,
  onClick,
  disabled = false,
  label,
  className = "",
}: OAuthButtonProps) {
  const config = PROVIDER_CONFIG[provider];
  const displayLabel = label || config.defaultLabel;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center justify-center border bg-white transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      style={{
        borderColor: colors.divider.mediumGray,
        height: modalSpacing.buttonHeight,
        maxWidth: modalSpacing.signInContentWidth,
      }}
    >
      <div className="flex items-center justify-center gap-3">
        <Image src={config.icon} alt="" width={16} height={16} className="flex-shrink-0" />
        <span className="text-button text-ink">{displayLabel}</span>
      </div>
    </button>
  );
}
